import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { ConfigService, SessionService, UtilsService } from 'src/app/services';
import { safeGet } from 'src/app/utilities/objects';
import { buildSentryDsn } from 'src/app/utilities/sentry';
import { environment } from 'src/environments/environment';

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
    user: {
        email: string,
        username: string,
        name: string
    };
    constructor(public sessionService: SessionService,
                configService: ConfigService,
                utilsService: UtilsService) {
        utilsService.getReleaseVersion().subscribe(release => {
            if (environment.environment.platform) {
                this.initSentry(environment.environment.platform, release);
            } else {
                configService.subscribe('environment', (env) => {
                    this.initSentry(env.platform, release);
                });
            }
        });
    }

    initSentry(platform: string, release: string) {
        Sentry.init({
            dsn: buildSentryDsn(),
            environment: platform,
            release: release,
            // ignoreErrors: ['Non-Error exception captured']

            // TODO leaving as reference if we need this back
            // TryCatch has to be configured to disable XMLHttpRequest wrapping, as we are going to handle
            // http module exceptions manually in Angular's ErrorHandler and we don't want it to capture the same error twice.
            // Please note that TryCatch configuration requires at least @sentry/browser v5.16.0.
            //      integrations: [
            //        // new Sentry.Integrations.TryCatch({
            //        //   XMLHttpRequest: false,
            //        // })
            //        inboundFiltersIntegration({
            //          XMLHttpRequest: false
            //        })
            //       ],
        });

        this.sessionService.userChanged.subscribe((user) => {
            this.user = {
                email: user.email,
                username: user.username,
                name: `${ safeGet('firstName', user, '') } ${ safeGet('lastName', user, '') }`
            };

            Sentry.setUser(this.user);
        });
    }

    extractError(error) {
        // Try to unwrap zone.js error.
        // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
        if (error && error.ngOriginalError) {
            error = error.ngOriginalError;
        }
        switch (true) {
            // We can handle messages and Error objects directly.
            case !!(typeof error === 'string' || error instanceof Error):
                return error;
            case !!safeGet('error.message', error):
                return error.error.message;
            case !!safeGet('error.clientMessage', error):
                return error.error.clientMessage;
            case !!safeGet('message', error):
                return error.message;
            default:
                // Skip if there's no error, and let user decide what to do with it.
                return null;
        }
    }

    handleError(error) {
        if (!environment.production) {
            console.error();
            return; // Ignore Local failures
        }

        if (this.isApiFailure(error) || this.isChunkError(error)) {
            return; // Ignore API failures because they are caught by backend.
        }

        const extractedError = this.extractError(error) || 'Handled unknown error';

        // Capture handled exception and send it to Sentry.
        Sentry.captureException(extractedError);

        console.error();

        // Optionally show user dialog to provide details on what happened.
        // Sentry.showReportDialog({ eventId, user: this.user });
    }

    isApiFailure(error) {
        return error instanceof HttpErrorResponse
            || error.ngOriginalError instanceof HttpErrorResponse
            || error.name === 'HttpErrorResponse';
    }

    isChunkError(error: any) {
        const chunkFailedMessage = /Loading chunk [\d]+ failed/;

        if (chunkFailedMessage.test(error.message)) {
            window.location.reload();
            return true;
        }

        return false;
    }
}
